import {
  Box,
  Grid,
  Input,
  Modal,
  Radio,
  Select,
  Button,
  HStack,
  useToast,
  FormLabel,
  ModalBody,
  RadioGroup,
  FormControl,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import { isValidEmail } from "../../../../libraries/utils";
import { MemberType } from '../../../../config/constant';
interface UserAddModalProps {
  isOpen: boolean,
  onClose: () => void,
  onSave: (e: {
    firstName: string,
    middleName: string,
    lastName: string,
    email: string,
    inviteType: string,
    relationType: string,
    connectType: 'UPLOAD' | 'CONNECT',
  }) => Promise<void>
}

const INVITE_TYPE = [{
  label: 'Admin',
  value: 'admin',
}, {
  label: 'Observer',
  value: 'observer',
}];

const AddMemberModal: React.FC<UserAddModalProps> = ({ isOpen, onClose, onSave }) => {
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState<string>('');
  const [relationType, setRelationType] = useState('');
  const [inviteType, setInviteType] = useState('');
  const [connectType, setConnectType] = useState<'CONNECT' | 'UPLOAD'>('CONNECT');

  const save = async () => {
    let message = null;

    if (!firstName) {
      message = 'First name is required.';
    } else if (!lastName) {
      message = 'Last name is required.';
    } else if (!email) {
      message = 'Email is required.';
    } else if (!isValidEmail(email)) {
      message = 'Invalid email address.';
    } else if (!relationType) {
      message = 'Relation is required.';
    }

    if (message) {
      toast({
        title: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setIsSaving(true);
      await onSave({
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        middleName,
        inviteType,
        connectType,
        relationType,
      });
      setIsSaving(false);
      handleClose();
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value);
  }

  const handleClose = () => {
    onClose();
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setEmail('');
  }

  return (
    <Modal isOpen={isOpen} onClose={() => handleClose()} size="xl" isCentered>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Add Prospect</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <Grid
            templateColumns={useBreakpointValue({ base: '1fr', md: 'repeat(3, 1fr)' })}
            gap={useBreakpointValue({ base: 4, md: 2 })}
          >
            <FormControl isRequired>
              <FormLabel>First Name</FormLabel>
              <Input
                placeholder='John'
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Middle Name</FormLabel>
              <Input
                placeholder='Fortune (Optional)'
                value={middleName}
                onChange={(e) => {
                  setMiddleName(e.target.value);
                }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input
                placeholder='Doe'
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
              />
            </FormControl>
          </Grid>
          <Grid
            mt="12px"
            gap={useBreakpointValue({ base: 4, md: 2 })}
            templateColumns={useBreakpointValue({ base: '1fr', md: 'repeat(2, 1fr)' })}
          >
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                colorScheme="brandScheme"
                placeholder='you@example.com'
                onChange={(e) => handleEmailChange(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Relationship</FormLabel>
              <Select
                value={relationType}
                onChange={(e) => {
                  setRelationType(e.target.value);
                }}
              >
                <option key={0} value=''>
                  Select a relation type...
                </option>
                {MemberType.map((relation) => (
                  <option key={relation.value} value={relation.value}>
                    {relation.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid mt="12px" gap={useBreakpointValue({ base: 4, md: 2 })}>
            <FormControl>
              <FormLabel>Invite Type</FormLabel>
              <Select
                value={inviteType}
                onChange={(e) => {
                  setInviteType(e.target.value);
                }}
              >
                <option key={0} value=''>
                  Select a invite type...
                </option>
                {INVITE_TYPE.map((invType) => (
                  <option key={invType.value} value={invType.value}>
                    {invType.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Box>
            <FormLabel mt={2}>Connect Type</FormLabel>
            <RadioGroup value={connectType} onChange={(e: 'CONNECT' | 'UPLOAD') => setConnectType(e)}>
              <HStack>
                <Radio
                  colorScheme="brandScheme"
                  fontSize='sm'
                  value='CONNECT'
                >
                  CONNECT
                </Radio>
                <Radio
                  colorScheme="brandScheme"
                  fontSize='sm'
                  value='UPLOAD'
                >
                  UPLOAD
                </Radio>
              </HStack>
            </RadioGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            mr={3}
            loadingText='Adding'
            isDisabled={isSaving}
            colorScheme="brandScheme"
            onClick={() => save()} isLoading={isSaving}
          >
            Add
          </Button>
          <Button
            variant="ghost"
            colorScheme="brandScheme"
            onClick={() => handleClose()}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddMemberModal;
